// Tier 2 Progress dashboard math helpers (#5–#8). All pure functions
// over already-fetched data. See docs/progress-redesign.md.
import { DAY_MS } from "./freshness.js";
import { rirToRpe, effectiveRir } from "./effortScale.js";
import type { LogSet } from "./schemas/logSet.js";
import type { TimedSessionResult } from "./progressTypes.js";

// =========================================================================
// Tier 2 #5 — Reps × intensity heatmap.
// =========================================================================
// 4×4 matrix counting weighted sets by (rep range, %1RM intensity).
// Rep buckets: 1-3 / 4-6 / 7-10 / 11+
// Intensity buckets: <50% / 50-70% / 70-85% / 85%+
// Non-weighted kinds are skipped. Fail sets are NOT: they count their
// reps like any other set (AP-0p model — `reps` is already the clean
// count and `fail` is a pure label), and this function has no branch on
// the field at all. The one fail-exclusion in this file is detectRirDrift
// below, which is deliberate and for a reason specific to RIR.
//
// An explicit userMaxKg is required to anchor the intensity scale: with
// none (userMaxKg <= 0) the matrix comes back empty rather than falling
// back to each set's own e1rmKg. That fallback would be circular — a
// set's e1rmKg IS its 1RM estimate for that rep count, so weight/e1rmKg
// reduces to 30/(30+reps) and says nothing about how hard the set was.
// Since e1rmKg is only populated for 1..E1RM_REP_CAP (12) reps, that
// ratio can only ever land in ~71-97%: every set would read "heavy".

export type RepsIntensityMatrix = number[][]; // [repsBucket][intensityBucket]

const repsBucketIdx = (reps: number): number => {
  if (reps <= 3) return 0;
  if (reps <= 6) return 1;
  if (reps <= 10) return 2;
  return 3;
};

const intensityBucketIdx = (pct: number): number => {
  if (pct < 0.5) return 0;
  if (pct < 0.7) return 1;
  if (pct < 0.85) return 2;
  return 3;
};

export const repsIntensityBuckets = (
  sets: ReadonlyArray<LogSet>,
  userMaxKg: number,
): RepsIntensityMatrix => {
  const matrix: RepsIntensityMatrix = [
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ];
  if (userMaxKg <= 0) return matrix;
  for (const s of sets) {
    if (s.kind !== "weighted") continue;
    // A fail counts its reps (AP-0p model: reps = clean, fail = pure label).
    if (!s.reps || s.reps < 1) continue;
    if (!s.weight || s.weight <= 0) continue;
    const pct = s.weight / userMaxKg;
    if (!Number.isFinite(pct) || pct <= 0) continue;
    const row = matrix[repsBucketIdx(s.reps)];
    if (!row) continue;
    const col = intensityBucketIdx(pct);
    row[col] = (row[col] ?? 0) + 1;
  }
  return matrix;
};

// =========================================================================
// Tier 2 #6 — Density trend.
// =========================================================================
// Per-session density (kg/min) ordered chronologically. Sessions
// without a densityKgPerMin (no endedAt) are skipped — they'd plot as
// gaps and the UI's rolling-avg line handles missing data gracefully.

export type DensityPoint = { atMs: number; kgPerMin: number };

export const densityTrend = (
  sessions: TimedSessionResult[],
  nowMs: number,
  windowDays: number,
): ReadonlyArray<DensityPoint> => {
  const cutoff = nowMs - windowDays * DAY_MS;
  return sessions
    .filter(
      (s) =>
        s.startedAtMs >= cutoff &&
        s.startedAtMs <= nowMs &&
        s.result.densityKgPerMin !== null &&
        s.result.densityKgPerMin > 0,
    )
    .sort((a, b) => a.startedAtMs - b.startedAtMs)
    .map((s) => ({
      atMs: s.startedAtMs,
      kgPerMin: s.result.densityKgPerMin!,
    }));
};

// =========================================================================
// Tier 2 #7 — Frequency per exercise.
// =========================================================================
// "Days since last logged" + "sets in the last 28 days" per exercise.
// Output sorted by daysSinceLast desc so the UI can render the
// stalest-first list at zero cost. Exercises with no completed sets
// excluded (no signal).

export type ExerciseFrequency = {
  slug: string;
  daysSinceLast: number;
  setsLast28d: number;
  lastPerformedAtMs: number;
};

export const exerciseFrequency = (
  sets: ReadonlyArray<LogSet>,
  nowMs: number,
): ReadonlyArray<ExerciseFrequency> => {
  const cutoff28d = nowMs - 28 * DAY_MS;
  const bySlug = new Map<
    string,
    { lastMs: number; setsLast28d: number }
  >();
  for (const s of sets) {
    // A fail is still a set performed (AP-0p model) → counts toward frequency.
    const tMs = Date.parse(s.performedAt);
    if (!Number.isFinite(tMs)) continue;
    const prev = bySlug.get(s.exerciseSlug);
    const lastMs = Math.max(prev?.lastMs ?? 0, tMs);
    const setsLast28d =
      (prev?.setsLast28d ?? 0) + (tMs >= cutoff28d ? 1 : 0);
    bySlug.set(s.exerciseSlug, { lastMs, setsLast28d });
  }
  const out: ExerciseFrequency[] = [];
  for (const [slug, v] of bySlug.entries()) {
    const daysSinceLast = Math.floor((nowMs - v.lastMs) / DAY_MS);
    out.push({
      slug,
      daysSinceLast,
      setsLast28d: v.setsLast28d,
      lastPerformedAtMs: v.lastMs,
    });
  }
  out.sort((a, b) => b.daysSinceLast - a.daysSinceLast);
  return out;
};

// =========================================================================
// Tier 2 #8 — Fatigue drift (RIR vs RPE).
// =========================================================================
// Rolling weekly buckets of mean RPE and mean RIR for sets where BOTH
// fields are present. The "rising fatigue" signal is RPE drifting up
// while RIR drifts down on similar loads — accumulated fatigue showing
// before any e1RM regression. Helper is pure over LogSet[]; the panel
// renders the chart and decides UI affordances.

export type FatigueDriftBucket = {
  bucketEndMs: number;
  avgRpe: number;
  avgRir: number;
  sampleCount: number;
};

export type FatigueDriftResult = {
  buckets: ReadonlyArray<FatigueDriftBucket>;
  // 'rising' when the last bucket has higher avgRpe AND lower avgRir
  // than the first bucket, with ≥2 buckets of data. 'stable' otherwise.
  // 'no-data' when fewer than 2 weeks of rpe+rir sets are in the window.
  flag: "rising" | "stable" | "no-data";
};

export const detectRirDrift = (
  sets: ReadonlyArray<LogSet>,
  nowMs: number,
  windowDays: number,
): FatigueDriftResult => {
  const cutoff = nowMs - windowDays * DAY_MS;
  const bucketMs = 7 * DAY_MS;
  const bucketCount = Math.max(1, Math.ceil(windowDays / 7));
  type Acc = { rpeSum: number; rirSum: number; n: number; endMs: number };
  const acc: Acc[] = [];
  for (let i = 0; i < bucketCount; i++) {
    acc.push({
      rpeSum: 0,
      rirSum: 0,
      n: 0,
      endMs: nowMs - i * bucketMs,
    });
  }
  for (const s of sets) {
    if (s.kind === "cardio" || s.kind === "timed") continue;
    // RIR-drift trend stays fail-excluded on purpose (AP-0p): a fail went to
    // failure and its logged RIR (if any) isn't a comparable in-reserve point.
    if (s.fail) continue;
    // Canonical effort via the single predicate (effectiveRir): rir ??
    // rpeToRir(rpe), accepting rir-only, rpe-only (legacy / RPE-scale loggers),
    // or both, instead of requiring both fields (which silently dropped every
    // one-canonical set). fail sets are already excluded above, so the fail⇒0
    // branch never fires here — this stays a fail-excluded in-reserve trend.
    const rir = effectiveRir(s);
    if (rir == null) continue;
    const ts = Date.parse(s.performedAt);
    if (!Number.isFinite(ts) || ts < cutoff || ts > nowMs) continue;
    const ageMs = nowMs - ts;
    const idx = Math.min(bucketCount - 1, Math.floor(ageMs / bucketMs));
    const slot = acc[idx];
    if (!slot) continue;
    // rir is the source of truth — derive rpe from it unconditionally so
    // avgRpe and avgRir stay exact reciprocals even on legacy lockstep
    // rows whose stored rpe diverged from a post-save rir edit.
    slot.rpeSum += rirToRpe(rir);
    slot.rirSum += rir;
    slot.n += 1;
  }
  const buckets: FatigueDriftBucket[] = acc
    .map((a) => ({
      bucketEndMs: a.endMs,
      avgRpe: a.n > 0 ? a.rpeSum / a.n : 0,
      avgRir: a.n > 0 ? a.rirSum / a.n : 0,
      sampleCount: a.n,
    }))
    .filter((b) => b.sampleCount > 0)
    .sort((a, b) => a.bucketEndMs - b.bucketEndMs);
  if (buckets.length < 2) {
    return { buckets, flag: "no-data" };
  }
  const first = buckets[0]!;
  const last = buckets[buckets.length - 1]!;
  const rising = last.avgRpe > first.avgRpe && last.avgRir < first.avgRir;
  return { buckets, flag: rising ? "rising" : "stable" };
};
